import { Alpine } from '../../core/alpine-core.js';
import { apiFetch } from '../api.js';
import { canManageTournamentDoc, canEndTournamentSessionDoc } from './tournament-access.js';

const BV_CSS = 'https://cdn.jsdelivr.net/npm/brackets-viewer@1.9.0/dist/brackets-viewer.min.css';
const BV_JS = 'https://cdn.jsdelivr.net/npm/brackets-viewer@1.9.0/dist/brackets-viewer.min.js';
const BV_SELECTOR = '#tournament-bracket-root .brackets-viewer';

function escapeHtml(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function pad2(n) {
  return String(n).padStart(2, '0');
}

function navTo(path) {
  const router = globalThis.PineconeRouter;
  if (router?.navigate) router.navigate(path);
  else globalThis.location.assign(path);
}

function toLocalDatetimeInputValue(iso) {
  const raw = String(iso || '').trim();
  if (!raw) return '';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

function fromLocalDatetimeInputValue(val) {
  const raw = String(val || '').trim();
  if (!raw) return '';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString();
}

function formatMatchWhen(iso) {
  const raw = String(iso || '').trim();
  if (!raw) return 'Date: TBC';
  try {
    return new Date(raw).toLocaleString('en-GB', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  } catch {
    return 'Date: TBC';
  }
}

function bracketsViewerApi() {
  const api = globalThis.bracketsViewer;
  if (api && typeof api.render === 'function') return api;
  return null;
}

function waitForBracketsViewer(timeoutMs = 12000) {
  const existing = bracketsViewerApi();
  if (existing) return Promise.resolve(existing);
  return new Promise((resolve, reject) => {
    const started = Date.now();
    const tick = () => {
      const api = bracketsViewerApi();
      if (api) return resolve(api);
      if (Date.now() - started >= timeoutMs) {
        return reject(new Error('Bracket viewer library failed to load. Refresh the page.'));
      }
      globalThis.setTimeout(tick, 50);
    };
    tick();
  });
}

function loadCssOnce(href) {
  if (document.querySelector(`link[data-bv-css="1"][href="${href}"]`)) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const l = document.createElement('link');
    l.rel = 'stylesheet';
    l.href = href;
    l.setAttribute('data-bv-css', '1');
    l.onload = () => resolve();
    l.onerror = () => reject(new Error('brackets-viewer css'));
    document.head.appendChild(l);
  });
}

function loadScriptOnce(src) {
  if (bracketsViewerApi()) return Promise.resolve();
  const existing = document.querySelector(`script[data-bv-js="1"][src="${src}"]`);
  if (existing) return waitForBracketsViewer();
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = src;
    s.async = true;
    s.setAttribute('data-bv-js', '1');
    s.onload = () => {
      waitForBracketsViewer().then(resolve).catch(reject);
    };
    s.onerror = () => reject(new Error('brackets-viewer js'));
    document.head.appendChild(s);
  });
}

Alpine.data('tournamentBracketPage', () => ({
  loading: true,
  patching: false,
  startingMatch: false,
  ending: false,
  error: '',
  bracketMissing: false,
  tournamentId: '',
  tournament: null,
  entries: [],
  entriesByParticipantId: {},
  myMatchIdsFromServer: [],
  sessionPlayerIdFromServer: '',
  viewerPayload: null,
  scheduleByMatchId: {},
  matchById: {},
  selectedMatchId: '',
  drawerOpen: false,
  organiserToolsOpen: false,
  venueQuery: '',
  venueSuggest: [],
  venuePicked: null,
  venuesById: {},
  _reloadGen: 0,
  edit: {
    matchId: '',
    status: 'pending',
    scheduledAtLocal: '',
    venueId: '',
    boardId: '',
    winnerParticipantId: '',
    scoreNote: '',
  },

  canManage() {
    return canManageTournamentDoc(this.tournament, this.$store?.session?.me);
  },

  canEndSession() {
    return canEndTournamentSessionDoc(this.tournament, this.$store?.session?.me);
  },

  tournamentEnded() {
    return String(this.tournament?.phase || '') === 'completed';
  },

  phaseDisplayLabel() {
    const ph = String(this.tournament?.phase || '');
    if (ph === 'completed') return 'Ended';
    return ph.replace(/_/g, ' ');
  },

  myPlayerId() {
    const me = this.$store?.session?.me || {};
    return String(
      this.sessionPlayerIdFromServer
        || this.$store?.session?.rosterSelfPlayerId
        || me.playerId
        || me.player?.playerId
        || '',
    ).trim();
  },

  myIdentityHints() {
    const me = this.$store?.session?.me || {};
    const hints = new Set();
    const pid = this.myPlayerId();
    if (pid) hints.add(pid);
    for (const v of [me.email, me.fullName, me.displayName, me.player?.displayName, me.player?.fullName]) {
      const s = String(v || '').trim().toLowerCase();
      if (s) hints.add(s);
    }
    return hints;
  },

  async init() {
    const path = window.location.pathname || '';
    const m = /\/tournaments\/([^/]+)\/bracket/.exec(path);
    if (!m) {
      this.error = 'Missing tournament';
      this.loading = false;
      return;
    }
    this.tournamentId = decodeURIComponent(m[1]);
    try {
      const u = new URL(globalThis.location.href);
      if (u.searchParams.get('highlight') === 'play') {
        this.$store?.toastr?.info?.('Your match is ready — choose local or remote play', 6000);
      }
    } catch { /* ignore */ }
    await this.reload(true);
    this.maybeAutoOpenPlayDrawer();
  },

  maybeAutoOpenPlayDrawer() {
    try {
      const u = new URL(globalThis.location.href);
      if (u.searchParams.get('highlight') !== 'play') return;
      const playable = this.myPlayableMatches();
      if (!playable.length) return;
      this.selectBracketMatch(playable[0].matchId, true);
    } catch { /* ignore */ }
  },

  indexEntries(items) {
    const map = {};
    for (const e of items || []) {
      const pid = e?.bracketParticipantId;
      if (pid == null) continue;
      map[String(pid)] = e;
    }
    this.entriesByParticipantId = map;
    this.entries = Array.isArray(items) ? items : [];
  },

  entryForParticipantId(participantId) {
    return this.entriesByParticipantId[String(participantId)] || null;
  },

  bracketStatusToDartsos(status) {
    const n = Number(status);
    if (n === 4) return 'completed';
    if (n === 3) return 'live';
    if (n === 2) return 'ready';
    if (n === 1) return 'scheduled';
    return 'pending';
  },

  getMatchMeta(matchId) {
    const mid = String(matchId || '').trim();
    const ov = (this.scheduleByMatchId && this.scheduleByMatchId[mid]) || {};
    const bm = this.matchById[mid] || null;
    const status = ov.status || this.bracketStatusToDartsos(bm?.status);
    const scheduledAtIso = ov.scheduledAt ? String(ov.scheduledAt) : '';
    const venueId = ov.venueId ? String(ov.venueId) : '';
    const venue = venueId ? this.venuesById[venueId] : null;
    const venueLabel = venue?.name ? `Venue: ${venue.name}` : venueId ? `Venue: ${venueId}` : 'Venue: TBC';
    let suggestedWinnerId = '';
    if (bm?.opponent1?.result === 'win' && bm.opponent1?.id != null) suggestedWinnerId = String(bm.opponent1.id);
    else if (bm?.opponent2?.result === 'win' && bm.opponent2?.id != null) suggestedWinnerId = String(bm.opponent2.id);
    const hasBoth = bm?.opponent1?.id != null && bm?.opponent2?.id != null;
    return {
      matchId: mid,
      status,
      statusLabel: status,
      scheduledAtIso,
      whenLabel: formatMatchWhen(scheduledAtIso),
      venueId,
      venueName: venue?.name || '',
      venueLabel,
      boardId: ov.boardId ? String(ov.boardId) : '',
      scoreNote: ov.scoreNote ? String(ov.scoreNote) : '',
      playMode: ov.playMode || this.tournament?.drawConfig?.playMode || 'local',
      sessionId: ov.sessionId || null,
      suggestedWinnerId,
      hasBothOpponents: hasBoth,
      playBadge: this.playBadgeLabel(mid),
      isMyMatch: this.isMyMatch(mid),
    };
  },

  matchOpponents(matchId) {
    const bm = this.matchById[String(matchId)] || null;
    if (!bm) return { a: null, b: null };
    const aId = bm.opponent1?.id;
    const bId = bm.opponent2?.id;
    return {
      a: aId != null ? this.entryForParticipantId(aId) : null,
      b: bId != null ? this.entryForParticipantId(bId) : null,
      aName: bm.opponent1?.name || this.entryForParticipantId(aId)?.displayName || 'TBC',
      bName: bm.opponent2?.name || this.entryForParticipantId(bId)?.displayName || 'TBC',
    };
  },

  isMyMatch(matchId) {
    const mid = String(matchId || '').trim();
    if (!mid) return false;
    if (this.canManage()) return true;
    if (this.myMatchIdsFromServer.includes(mid)) return true;
    const hints = this.myIdentityHints();
    const { a, b } = this.matchOpponents(mid);
    for (const ent of [a, b].filter(Boolean)) {
      const refs = (ent.refIds || []).map(String);
      if (refs.some((id) => hints.has(id))) return true;
      const dn = String(ent.displayName || '').trim().toLowerCase();
      if (dn && hints.has(dn)) return true;
    }
    return false;
  },

  canStartMatch(matchId) {
    if (this.tournamentEnded()) return false;
    const meta = this.getMatchMeta(matchId);
    if (!meta.hasBothOpponents) return false;
    if (meta.status === 'completed') return false;
    if (!this.isMyMatch(matchId) && !this.canManage()) return false;
    return ['ready', 'scheduled', 'pending', 'live'].includes(meta.status);
  },

  canResumeMatch(matchId) {
    const meta = this.getMatchMeta(matchId);
    if (!this.isMyMatch(matchId)) return false;
    return meta.status === 'live' || !!meta.sessionId;
  },

  myPlayableMatches() {
    const out = [];
    for (const mid of Object.keys(this.matchById)) {
      const meta = this.getMatchMeta(mid);
      if (!meta.hasBothOpponents || meta.status === 'completed') continue;
      if (this.isMyMatch(mid) || (this.canManage() && meta.status !== 'completed')) {
        if (this.canStartMatch(mid) || this.canResumeMatch(mid)) out.push(meta);
      }
    }
    return out.filter((m) => this.isMyMatch(m.matchId));
  },

  heroMatch() {
    const mine = this.myPlayableMatches();
    if (mine.length) return mine[0];
    return null;
  },

  selectedMatch() {
    if (!this.selectedMatchId) return null;
    return this.getMatchMeta(this.selectedMatchId);
  },

  playBadgeLabel(matchId) {
    const mid = String(matchId || '').trim();
    const ov = (this.scheduleByMatchId && this.scheduleByMatchId[mid]) || {};
    const bm = this.matchById[mid] || null;
    const status = ov.status || this.bracketStatusToDartsos(bm?.status);
    const hasBoth = bm?.opponent1?.id != null && bm?.opponent2?.id != null;
    const mine = this.isMyMatch(mid);
    if (status === 'completed') return 'Done';
    if (status === 'live') return mine ? 'Your match · Live' : 'Live';
    if (mine && hasBoth) return 'Your match';
    if (hasBoth && ['ready', 'scheduled', 'pending'].includes(status)) return 'Playable';
    return '';
  },

  matchPlayUrl(matchId, surface = 'score') {
    const tid = encodeURIComponent(this.tournamentId);
    const mid = encodeURIComponent(matchId);
    return `/admin/tournaments/${tid}/matches/${mid}/${surface}`;
  },

  async startMatch(playMode = 'local') {
    if (this.tournamentEnded()) {
      this.$store?.toastr?.warning?.('This tournament has ended');
      return;
    }
    const mid = String(this.selectedMatchId || this.heroMatch()?.matchId || '').trim();
    if (!mid) return;
    const meta = this.getMatchMeta(mid);
    if (!this.isMyMatch(mid)) {
      this.$store?.toastr?.warning?.('You are not listed in this match');
      return;
    }
    if (!meta.hasBothOpponents) {
      this.$store?.toastr?.warning?.('Both players must be assigned before play can start');
      return;
    }
    if (meta.status === 'completed') {
      this.$store?.toastr?.warning?.('This match is already complete');
      return;
    }
    if (!this.canStartMatch(mid) && !this.canResumeMatch(mid)) {
      this.$store?.toastr?.warning?.('This match is not ready to play yet');
      return;
    }
    this.startingMatch = true;
    try {
      const res = await apiFetch(
        `/tournaments/${encodeURIComponent(this.tournamentId)}/matches/${encodeURIComponent(mid)}/start`,
        { method: 'POST', body: { playMode: playMode === 'remote' ? 'remote' : 'local' } },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok === false) throw new Error(data.message || data.error || 'Start failed');
      if (data.match?.schedule && this.scheduleByMatchId) {
        this.scheduleByMatchId = { ...this.scheduleByMatchId, [mid]: data.match.schedule };
      }
      navTo(this.matchPlayUrl(mid, 'score'));
    } catch (e) {
      this.$store?.toastr?.error?.(e.message || String(e));
    } finally {
      this.startingMatch = false;
    }
  },

  buildMatchIndex(matches) {
    const map = {};
    for (const m of matches || []) {
      if (m?.id != null) map[String(m.id)] = m;
    }
    this.matchById = map;
  },

  indexVenues(items) {
    const map = { ...this.venuesById };
    for (const v of items || []) {
      const id = String(v?._id || v?.id || '').trim();
      if (!id) continue;
      map[id] = { _id: id, name: String(v.name || 'Venue').trim() || 'Venue', address: v.address || '' };
    }
    this.venuesById = map;
  },

  async loadVenueDirectory() {
    try {
      const res = await apiFetch('/venues?limit=500');
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return;
      this.indexVenues(Array.isArray(data.items) ? data.items : []);
    } catch { /* optional */ }
  },

  async searchVenues() {
    const q = String(this.venueQuery || '').trim();
    if (!q) {
      this.venueSuggest = [];
      return;
    }
    try {
      const res = await apiFetch(`/venues?q=${encodeURIComponent(q)}&limit=20`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Venue search failed');
      const items = Array.isArray(data.items) ? data.items : [];
      this.venueSuggest = items;
      this.indexVenues(items);
    } catch (e) {
      this.$store?.toastr?.error?.(e.message || 'Venue search failed');
      this.venueSuggest = [];
    }
  },

  pickVenue(v) {
    const id = String(v?._id || v?.id || '').trim();
    if (!id) return;
    this.edit.venueId = id;
    this.venuePicked = { _id: id, name: String(v.name || 'Venue').trim() || 'Venue' };
    this.venuesById = { ...this.venuesById, [id]: this.venuePicked };
    this.venueQuery = '';
    this.venueSuggest = [];
  },

  clearVenue() {
    this.edit.venueId = '';
    this.venuePicked = null;
  },

  selectBracketMatch(matchId, openDrawer = true) {
    const mid = String(matchId ?? '').trim();
    if (!mid) return;
    this.selectedMatchId = mid;
    this.drawerOpen = openDrawer;
    const meta = this.getMatchMeta(mid);
    this.edit.matchId = mid;
    this.edit.status = meta.status === 'ready' ? 'ready' : meta.status;
    this.edit.scheduledAtLocal = toLocalDatetimeInputValue(meta.scheduledAtIso);
    this.edit.venueId = meta.venueId;
    this.edit.boardId = meta.boardId;
    this.edit.scoreNote = meta.scoreNote;
    this.edit.winnerParticipantId = meta.suggestedWinnerId;
    if (meta.venueId) {
      this.venuePicked = this.venuesById[meta.venueId] || { _id: meta.venueId, name: meta.venueName || meta.venueId };
    } else {
      this.venuePicked = null;
    }
    this.venueQuery = '';
    this.venueSuggest = [];
    this.decorateBracketMatches();
  },

  decorateBracketMatches() {
    const root = document.querySelector('#tournament-bracket-root');
    if (!root) return;
    root.querySelectorAll('.match[data-match-id]').forEach((el) => {
      const id = el.getAttribute('data-match-id');
      if (!id) return;
      const meta = this.getMatchMeta(id);
      el.classList.toggle('bracket-match-selected', String(this.selectedMatchId) === String(id));
      el.classList.toggle('bracket-match-yours', meta.isMyMatch);
      el.classList.toggle('bracket-match-playable', !!meta.playBadge && meta.status !== 'completed');
      let box = el.querySelector('.dartsos-match-meta');
      if (!box) {
        box = document.createElement('div');
        box.className = 'dartsos-match-meta';
        const opponents = el.querySelector('.opponents');
        if (opponents) opponents.appendChild(box);
        else el.appendChild(box);
      }
      const badge = meta.playBadge
        ? `<span class="tournament-bracket-play-badge tournament-bracket-play-badge--${escapeHtml(meta.status)}">${escapeHtml(meta.playBadge)}</span>`
        : '';
      box.innerHTML = [
        badge,
        `<div class="dartsos-match-meta__status">${escapeHtml(meta.statusLabel)}</div>`,
        `<div class="dartsos-match-meta__when">${escapeHtml(meta.whenLabel)}</div>`,
        `<div class="dartsos-match-meta__venue">${escapeHtml(meta.venueLabel)}</div>`,
      ].join('');
    });
  },

  async renderBracketViewer(viewerTables) {
    const api = await waitForBracketsViewer();
    const v = viewerTables || {};
    await api.render(
      {
        stages: v.stage,
        matches: v.match,
        matchGames: v.match_game,
        participants: v.participant,
      },
      {
        clear: true,
        selector: BV_SELECTOR,
        onMatchClick: (match) => this.selectBracketMatch(match?.id),
        showPopoverOnMatchLabelClick: false,
      },
    );
    await this.$nextTick();
    this.decorateBracketMatches();
  },

  async reload(first = false) {
    const gen = ++this._reloadGen;
    if (!first) this.loading = true;
    this.error = '';
    this.bracketMissing = false;
    try {
      await this.loadVenueDirectory();
      const res = await apiFetch(`/tournaments/${encodeURIComponent(this.tournamentId)}/bracket`);
      const data = await res.json().catch(() => ({}));
      if (gen !== this._reloadGen) return;
      if (!res.ok || data.ok === false) {
        if (data.error === 'bracket_not_generated') {
          this.tournament = data.tournament || this.tournament;
          if (!this.tournament?.title) {
            const meta = await apiFetch(`/tournaments/${encodeURIComponent(this.tournamentId)}`);
            const metaJson = await meta.json().catch(() => ({}));
            if (meta.ok && metaJson.tournament) this.tournament = metaJson.tournament;
          }
          this.bracketMissing = true;
          this.error = this.canManage()
            ? 'This tournament does not have a bracket yet. Open the builder, add entrants if needed, then generate the draw.'
            : 'The draw has not been published yet. Check back here once the organiser generates the bracket.';
          return;
        }
        throw new Error(data.message || data.error || 'Bracket unavailable');
      }
      this.tournament = data.tournament;
      this.indexEntries(data.entries);
      this.myMatchIdsFromServer = Array.isArray(data.myMatchIds) ? data.myMatchIds.map(String) : [];
      this.sessionPlayerIdFromServer = data.sessionPlayerId ? String(data.sessionPlayerId) : '';
      this.viewerPayload = data.viewer;
      this.scheduleByMatchId =
        data.scheduleByMatchId && typeof data.scheduleByMatchId === 'object'
          ? data.scheduleByMatchId
          : this.tournament?.scheduleByMatchId || {};
      this.buildMatchIndex(data.viewer?.match);
      await loadCssOnce(BV_CSS);
      await loadScriptOnce(BV_JS);
      if (gen !== this._reloadGen) return;
      await this.$nextTick();
      const root = document.querySelector(BV_SELECTOR);
      if (!root) throw new Error('Bracket mount missing');
      await this.renderBracketViewer(data.viewer);
      this.error = '';
    } catch (e) {
      if (gen !== this._reloadGen) return;
      this.error = e.message || String(e);
    } finally {
      if (gen === this._reloadGen) this.loading = false;
    }
  },

  async endTournamentSession() {
    if (!this.tournamentId || !this.canEndSession()) return;
    if (!globalThis.confirm('End this tournament now? Active matches will stop.')) return;
    this.ending = true;
    try {
      const res = await apiFetch(`/tournaments/${encodeURIComponent(this.tournamentId)}/end`, { method: 'POST', body: {} });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok === false) throw new Error(data.message || data.error || 'Could not end tournament');
      this.tournament = data.tournament || this.tournament;
      this.$store?.toastr?.success?.('Tournament ended');
      await this.reload();
    } catch (e) {
      this.$store?.toastr?.error?.(e.message || String(e));
    } finally {
      this.ending = false;
    }
  },

  async patchMatch() {
    const mid = String(this.edit.matchId || '').trim();
    if (!mid) return this.$store?.toastr?.warning?.('Select a match on the bracket or enter a match id');
    this.patching = true;
    try {
      const body = {
        status: this.edit.status,
        venueId: this.edit.venueId ? String(this.edit.venueId).trim() : null,
        boardId: this.edit.boardId ? String(this.edit.boardId).trim() : null,
        scoreNote: this.edit.scoreNote ? String(this.edit.scoreNote).trim() : null,
      };
      const scheduledIso = fromLocalDatetimeInputValue(this.edit.scheduledAtLocal);
      body.scheduledAt = scheduledIso || null;
      if (this.edit.status === 'completed' && this.edit.winnerParticipantId !== '') {
        body.winnerParticipantId = Number(this.edit.winnerParticipantId);
      }
      const res = await apiFetch(`/tournaments/${encodeURIComponent(this.tournamentId)}/matches/${encodeURIComponent(mid)}`, {
        method: 'PATCH',
        body,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok === false) throw new Error(data.message || data.error || 'Update failed');
      this.viewerPayload = data.viewer;
      if (data.scheduleByMatchId && typeof data.scheduleByMatchId === 'object') {
        this.scheduleByMatchId = data.scheduleByMatchId;
      }
      this.buildMatchIndex(data.viewer?.match);
      await this.$nextTick();
      await this.renderBracketViewer(data.viewer);
      this.selectBracketMatch(mid);
      this.$store?.toastr?.success?.('Match updated');
    } catch (e) {
      this.$store?.toastr?.error?.(e.message || String(e));
    } finally {
      this.patching = false;
    }
  },
}));
